import React from "react";
import { Form, Utils } from "lexisnexis-form";
import { connect } from "react-redux";
import { Badge, Button, Col, Form as BootstrapForm, Modal, Nav, Row } from "react-bootstrap";
import _ from "lodash";
/*
import { setEditFormConfig, setEditFormConfigActionCreator } from "../../../../reducers/FormConfiguration/actions";
import { FormConfiguration } from "../../../../business-objects/FormConfiguration";
import { Form as FormDefinition } from "../../../../business-objects/FormDefinition/Form";


interface ValidationReduxProps {
  editFormConfig: FormConfiguration;
  submission;
  form: FormDefinition.Model;
}

interface ValidationDispatchProps {
  setEditFormConfig: setEditFormConfigActionCreator;
}

interface ValidationProps extends ValidationReduxProps, ValidationDispatchProps {}
*/

interface ValidationProps {
  submission?:any;
  form: any;
  setValidation: (validation:any) => void;
  previousValidation?: any[];
}

interface ValidationState {
  submission?: any;
  form?: any;
  searchText: string;
  statusFilter: string;
  viewMode: 'all' | 'pending';
  sampleData: string;
  testResults?: any[];
  testError?: string;
  jsonPreviewSummary?: ValidationSummary;
}

interface ValidationIssue {
  severity: 'error' | 'warning';
  message: string;
}

interface ValidationSummary {
  id: string;
  index: number;
  displayNumber: string;
  target: string;
  targetLabel: string;
  description: string;
  summary: string;
  message: string;
  type: string;
  disabled: boolean;
  issues: ValidationIssue[];
  searchableText: string;
  changeStatus?: 'new' | 'edited';
  rawRule?: any;
}

const operatorLabels = {
  formComponentValue: 'form field',
  nestedComponentValue: 'nested field',
  parentComponentValue: 'parent field',
  value: 'current value',
  index: 'row index',
  text: 'text',
  number: 'number',
  true: 'true',
  false: 'false',
  null: 'blank',
  list: 'list',
  assertionList: 'validation group',
  assert: 'assert',
  errorMessage: 'show message',
  if: 'if',
  equalTo: 'equals',
  notEqualTo: 'does not equal',
  and: 'and',
  or: 'or',
  not: 'not',
  toBoolean: 'has value',
  greaterThan: 'greater than',
  lessThan: 'less than',
  greaterThanOrEqualTo: 'greater than or equal to',
  lessThanOrEqualTo: 'less than or equal to',
  betweenExclusive: 'between exclusive',
  betweenInclusive: 'between inclusive',
  max: 'maximum',
  min: 'minimum',
  abs: 'absolute value',
  add: 'add',
  subtract: 'subtract',
  negate: 'negate',
  multiply: 'multiply',
  divide: 'divide',
  modulo: 'modulo',
  merge: 'merge',
  includes: 'includes',
  concatenate: 'combine text',
  substr: 'substring',
  range: 'range',
  item: 'item',
  filter: 'filter',
  all: 'all',
  some: 'some',
  length: 'length',
  isEmpty: 'is empty',
  dateIsNotEmpty: 'date is not empty',
  getByIndex: 'get by index',
  getFormPath: 'form path',
  getNestedPath: 'nested path',
  getByPath: 'get by path',
  relativePath: 'relative path',
  getYearsBetween: 'years between',
  getCourtDate: 'court date',
  getStartingPointDate: 'starting point date',
  compareDates: 'same date',
  getFirstAvailableDate: 'first available date',
  binaryConjunction: 'binary conjunction',
  isActCompulsory: 'act compulsory',
  notIsEmpty: 'is not empty',
  courtAppearanceDateCheck: 'court appearance date check',
  isDateNotGreaterThanCurrent: 'date is not after today',
  isTimeNotGreaterThanCurrent: 'time is not after now',
  isDateAfter: 'date is after',
  getUniqueValues: 'unique values',
  getYearFromDate: 'date part',
  getCurrentDate: 'current date',
  matchPattern: 'matches pattern',
  pattern: 'pattern',
  matchMask: 'matches mask',
  getIndex: 'get index',
  splitStringToArray: 'split text',
  compareStringsCaseInsensitive: 'same text ignoring case',
  toUpperCase: 'uppercase',
};

const getOperatorLabel = (operator: string) => operatorLabels[operator] || _.startCase(operator || 'Incomplete');

const normalizeValidationValue = (value: any): any[] => {
  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    try {
      return normalizeValidationValue(JSON.parse(value));
    } catch (error) {
      return [];
    }
  }

  if (Array.isArray(value)) {
    return value;
  }

  const wrappedValidation = _.get(value, 'data.validation');
  if (wrappedValidation) {
    return normalizeValidationValue(wrappedValidation);
  }

  if (value.validation) {
    return normalizeValidationValue(value.validation);
  }

  if (typeof value === 'object') {
    return _.values(value).filter(item => item && (item.target || item.logic || item.description));
  }

  return [];
};

const createValidationSubmission = (form: any, submission?: any) => {
  const submissionValidation = submission ? normalizeValidationValue(_.get(submission, 'data.validation')) : [];
  const formValidation = normalizeValidationValue(_.get(form, 'properties.validation'));
  const validation = submissionValidation.length > 0 ? submissionValidation : formValidation;

  return {
    ...(submission || {}),
    data: {
      ..._.get(submission, 'data', {}),
      validation,
    },
  };
};

const getValidationList = (submission: any): any[] => {
  return normalizeValidationValue(_.get(submission, 'data.validation', []));
};

const getComponentLabel = (components: any[], value: string) => {
  const match = components.find(component => component.value === value);
  return match ? match.label : value || 'No target selected';
};

const getNodeChildren = (node: any): any[] => Array.isArray(node && node.children) ? node.children : [];

const getNodeData = (node: any): any => _.get(node, 'data', {});

const getNodeValueLabel = (node: any, components: any[]) => {
  const data = getNodeData(node);
  switch (data.operator) {
    case 'formComponentValue':
    case 'getFormPath':
      return getComponentLabel(components, data.formComponent);
    case 'nestedComponentValue':
    case 'getNestedPath':
      return _.get(data, 'nestedComponent.label') || _.get(data, 'nestedComponent.value') || 'nested field';
    case 'parentComponentValue':
      return _.get(data, 'parentComponent.label') || _.get(data, 'parentComponent.value') || 'parent field';
    case 'text':
      return `"${data.text || ''}"`;
    case 'number':
      return _.isNil(data.number) ? 'number' : String(data.number);
    case 'pattern':
      return getComponentLabel(Utils.ValidationService.Patterns || [], data.pattern);
    case 'errorMessage':
      return data.errorMessage || 'Missing message';
    case 'value':
    case 'index':
    case 'true':
    case 'false':
    case 'null':
    case 'getCurrentDate':
      return getOperatorLabel(data.operator);
    default:
      return '';
  }
};

const summarizeNode = (node: any, components: any[], depth = 0): string => {
  if (!node || depth > 5) {
    return 'Incomplete rule';
  }

  const data = getNodeData(node);
  const operator = data.operator;
  const children = getNodeChildren(node);
  const valueLabel = getNodeValueLabel(node, components);

  if (!operator) {
    return 'Incomplete rule';
  }

  if (valueLabel) {
    return valueLabel;
  }

  if (operator === 'assert') {
    const condition = summarizeNode(children[0], components, depth + 1);
    const message = summarizeNode(children[1], components, depth + 1);
    return `${data.disabled ? 'Disabled: ' : ''}Require ${condition}; otherwise show ${message}`;
  }

  if (operator === 'if') {
    const condition = summarizeNode(children[0], components, depth + 1);
    const whenTrue = summarizeNode(children[1], components, depth + 1);
    const whenFalse = summarizeNode(children[2], components, depth + 1);
    return `If ${condition}, then ${whenTrue}${children[2] ? `, else ${whenFalse}` : ''}`;
  }

  if (['and', 'or', 'assertionList', 'list'].includes(operator)) {
    return children.map(child => summarizeNode(child, components, depth + 1)).join(` ${getOperatorLabel(operator)} `);
  }

  if (operator === 'not') {
    return `not ${summarizeNode(children[0], components, depth + 1)}`;
  }

  const childSummaries = children.map(child => summarizeNode(child, components, depth + 1)).filter(Boolean);
  return childSummaries.length > 0
    ? `${getOperatorLabel(operator)} ${childSummaries.join(', ')}`
    : getOperatorLabel(operator);
};

const collectNodes = (node: any): any[] => {
  if (!node) {
    return [];
  }

  return [node].concat(_.flatMap(getNodeChildren(node), collectNodes));
};

const getFirstMessageNode = (node: any) => collectNodes(node).find(item => getNodeData(item).operator === 'errorMessage');

const getRuleType = (node: any) => {
  const messageNode = getFirstMessageNode(node);
  return _.get(messageNode, 'data.warning') ? 'warning' : 'error';
};

const getRuleMessage = (node: any) => _.get(getFirstMessageNode(node), 'data.errorMessage', '');

const isRuleDisabled = (node: any) => collectNodes(node).some(item => getNodeData(item).operator === 'assert' && getNodeData(item).disabled);

const getNodeIssues = (rule: any, components: any[]): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const knownComponentValues = components.map(component => component.value);
  const nodes = collectNodes(rule.logic);
  const messageNode = getFirstMessageNode(rule.logic);

  if (!rule.target) {
    issues.push({ severity: 'error', message: 'Missing target component.' });
  } else if (!knownComponentValues.includes(rule.target)) {
    issues.push({ severity: 'error', message: `Target component no longer exists: ${rule.target}` });
  }

  if (!messageNode) {
    issues.push({ severity: 'warning', message: 'No error or warning message found.' });
  } else {
    const messageData = getNodeData(messageNode);
    if (!messageData.errorMessage) {
      issues.push({ severity: 'warning', message: 'Message node is missing text.' });
    }
    const errorComponents = Array.isArray(messageData.errorComponents) ? messageData.errorComponents : [];
    if (errorComponents.length === 0) {
      issues.push({ severity: 'warning', message: 'Message is not attached to any component.' });
    }
    errorComponents
      .filter(component => !knownComponentValues.includes(component))
      .forEach(component => issues.push({ severity: 'error', message: `Message references deleted component: ${component}` }));
  }

  nodes.forEach(node => {
    const data = getNodeData(node);
    const limits = data.operator && Utils.ValidationService.getNodeLimits(node);
    if (data.operator && limits && limits.min > getNodeChildren(node).length) {
      issues.push({ severity: 'warning', message: `${getOperatorLabel(data.operator)} is missing required child logic.` });
    }
    if (['formComponentValue', 'getFormPath'].includes(data.operator) && !knownComponentValues.includes(data.formComponent)) {
      issues.push({ severity: 'error', message: `Logic references deleted component: ${data.formComponent || '(blank)'}` });
    }
  });

  return issues;
};

const buildValidationSummary = (rule: any, components: any[], index: number, id: string, displayNumber: string, rawRule?: any): ValidationSummary => {
  const targetLabel = getComponentLabel(components, rule.target);
  const description = rule.description || '';
  const summary = summarizeNode(rule.logic, components);
  const message = getRuleMessage(rule.logic);
  const type = getRuleType(rule.logic);
  const disabled = isRuleDisabled(rule.logic);
  const issues = getNodeIssues(rule, components);
  const searchableText = [targetLabel, rule.target, description, summary, message, type, disabled ? 'disabled' : 'enabled']
    .join(' ')
    .toLowerCase();

  return {
    id,
    index,
    displayNumber,
    target: rule.target,
    targetLabel,
    description,
    summary,
    message,
    type,
    disabled,
    issues,
    searchableText,
    rawRule: rawRule !== undefined ? rawRule : rule,
  };
};

const buildValidationSummaries = (validation: any[], components: any[]): ValidationSummary[] => _.flatMap(validation, (rule, index) => {
  const logicData = getNodeData(rule.logic);
  const children = getNodeChildren(rule.logic);

  if (logicData.operator === 'assertionList' && children.length > 0) {
    return children.map((child, childIndex) => buildValidationSummary(
      {
        ...rule,
        description: _.get(child, 'data.description') || rule.description,
        logic: child,
      },
      components,
      index,
      `${index}-${childIndex}`,
      `${index + 1}.${childIndex + 1}`,
      rule,
    ));
  }

  return [buildValidationSummary(rule, components, index, `${index}`, `${index + 1}`, rule)];
});

// Stable identity key for a validation summary: target + description. Matches the key used by the
// field-history comparison tool so "same rule" means the same thing everywhere.
const getSummaryKey = (summary: ValidationSummary): string =>
  `${(summary.target || '').toLowerCase()}||${(summary.description || '').trim().toLowerCase()}`;

// Content signature used to decide whether a matched rule was edited. Includes the meaningful,
// user-visible fields (not presentation-only or audit metadata).
const getSummarySignature = (summary: ValidationSummary): string =>
  JSON.stringify([
    summary.target || '',
    (summary.description || '').trim(),
    summary.summary || '',
    summary.message || '',
    summary.type || '',
    !!summary.disabled,
  ]);

// Annotates current summaries with a changeStatus ('new' | 'edited') by matching each against the
// baseline (previous saved revision) summaries by target + description. Rules with no baseline
// match are 'new'; matched rules whose signature differs are 'edited'. When there is no baseline at
// all (first revision / no prior version), every current rule is treated as 'new'.
export const annotateChangeStatus = (
  current: ValidationSummary[],
  baseline: ValidationSummary[] | undefined,
): ValidationSummary[] => {
  if (!baseline) {
    return current.map(summary => ({ ...summary, changeStatus: 'new' as const }));
  }

  const baselineByKey = _.groupBy(baseline, getSummaryKey);

  return current.map(summary => {
    const matches = baselineByKey[getSummaryKey(summary)];
    if (!matches || matches.length === 0) {
      return { ...summary, changeStatus: 'new' as const };
    }
    const currentSignature = getSummarySignature(summary);
    const unchanged = matches.some(match => getSummarySignature(match) === currentSignature);
    return unchanged ? summary : { ...summary, changeStatus: 'edited' as const };
  });
};

// Builds the component option list (form + flattened components) used as the lookup table for
// summarizing validation rules. Mirrors the instance getComponents() method so the summaries can
// be produced outside the live Form Admin editor (e.g. from a stored form revision).
export const buildValidationComponents = (form: any): any[] => [
  {
    label: 'Form',
    value: 'form',
    component: form,
  },
].concat(
  _.map(Utils.flattenComponents((form && form.components) || [], true), (component: any, path: string) => ({
    label: `${component.label || component.key} (${path})`,
    value: path,
    component,
  })),
);

// Produces the format-matching validation summaries for a parsed form definition, limited to
// rules related to the supplied field (by target key/label match or logic form-component
// reference). Reused by the Form Field History page to render the same cards as Form Admin.
export const buildValidationSummariesForField = (form: any, fieldName: string): ValidationSummary[] => {
  const components = buildValidationComponents(form);
  const validation = normalizeValidationValue(_.get(form, 'properties.validation'));
  const summaries = buildValidationSummaries(validation, components);
  const term = (fieldName || '').trim().toLowerCase();

  if (!term) {
    return summaries;
  }

  return summaries.filter(summary =>
    (summary.target || '').toLowerCase().includes(term) ||
    (summary.targetLabel || '').toLowerCase().includes(term) ||
    summary.searchableText.includes(term),
  );
};

// Returns the raw form-level validation rule object at the given index of properties.validation.
// Used by the copy feature to carry the exact rule JSON (not just the display summary) between forms.
export const getRawValidationRule = (form: any, index: number): any => {
  const validation = normalizeValidationValue(_.get(form, 'properties.validation'));
  return validation[index];
};

// Returns the set of component keys (paths) that exist in a form, mirroring the lookup table used
// to summarize rules. Used by the reference-integrity check when copying a rule to another form.
export const getFormComponentKeys = (form: any): Set<string> =>
  new Set(buildValidationComponents(form).map(component => component.value));

// Collects every component key a rule references: its target plus any formComponent references in
// the logic tree and any errorComponents on message nodes. Used to verify a copied rule's
// references all exist in the destination form.
export const collectRuleReferencedKeys = (rule: any): string[] => {
  const keys: string[] = [];
  if (rule && rule.target) {
    keys.push(rule.target);
  }
  collectNodes(rule && rule.logic).forEach(node => {
    const data = getNodeData(node);
    if (['formComponentValue', 'getFormPath'].includes(data.operator) && data.formComponent) {
      keys.push(data.formComponent);
    }
    const errorComponents = Array.isArray(data.errorComponents) ? data.errorComponents : [];
    errorComponents.forEach((component: string) => keys.push(component));
  });
  return _.uniq(keys.filter(Boolean));
};

interface ValidationSummaryListProps {
  summaries: ValidationSummary[];
  onView?: (summary: ValidationSummary) => void;
}

// Presentational single rule card extracted so the exact same markup/format (badges, issues, etc.)
// can be reused in the Form Admin editor and the field-history comparison view.
export const ValidationSummaryCard: React.FC<{ summary: ValidationSummary; className?: string; onView?: (summary: ValidationSummary) => void }> = ({ summary, className, onView }) => (
  <div className={`validation-rule validation-rule-${summary.type}${className ? ` ${className}` : ''}`}>
    <div className='validation-rule-header'>
      <div>
        <span className='validation-rule-title'>
          {summary.description || `Validation ${summary.displayNumber}`}
        </span>
        {summary.disabled && <Badge variant='dark'>Disabled</Badge>}
        <Badge variant={summary.type === 'warning' ? 'warning' : 'danger'}>{summary.type}</Badge>
        {summary.changeStatus === 'new' && <Badge variant='success'>New</Badge>}
        {summary.changeStatus === 'edited' && <Badge variant='primary'>Edited</Badge>}
      </div>
      <div className='validation-rule-header-actions'>
        {onView && (
          <button
            type='button'
            className='validation-rule-view-btn'
            title='View the JSON that will be written'
            aria-label='View rule JSON'
            onClick={() => onView(summary)}
          >
            <i className='fa fa-eye' aria-hidden='true' />
          </button>
        )}
        <span className='validation-rule-number'>#{summary.displayNumber}</span>
      </div>
    </div>
    <div className='validation-rule-summary'>{summary.summary}</div>
    {summary.message && <div className='validation-rule-message'>{summary.message}</div>}
    {summary.issues.length > 0 && (
      <ul className='validation-issues'>
        {summary.issues.map((issue, issueIndex) => (
          <li className={`validation-issue-${issue.severity}`} key={`${summary.id}-${issueIndex}`}>
            {issue.message}
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Presentational card list extracted from renderSummaryWorkspace so the same markup/format can be
// reused outside the Form Admin editor.
export const ValidationSummaryList: React.FC<ValidationSummaryListProps> = ({ summaries, onView }) => {
  const groupedSummaries = _.groupBy(summaries, summary => summary.targetLabel);

  return (
    <div className='validation-index'>
      {summaries.length === 0 && (
        <div className='validation-empty'>No validations match the current search and filter.</div>
      )}
      {Object.entries(groupedSummaries).map(([targetLabel, targetSummaries]) => (
        <section className='validation-target-group' key={targetLabel}>
          <div className='validation-target-heading'>
            <span>{targetLabel}</span>
            <Badge variant='light'>{targetSummaries.length}</Badge>
          </div>
          {targetSummaries.map(summary => (
            <ValidationSummaryCard summary={summary} onView={onView} key={`${summary.target}-${summary.id}`} />
          ))}
        </section>
      ))}
    </div>
  );
};

const validationFormFactory = (components, arrayComponentsMap, parentComponentsMap) => ({
  display: 'form',
  components: [
    {
      label: 'Validation',
      tableView: true,
      type: 'editgrid',
      input: true,
      key: 'validation',
      inlineEdit: true,
      templates: {
        header: (
          `<div class='row'>
            <div class='col-sm-2'>{{ components[0].label }}</div>
            <div class='col-sm-2'>{{ components[1].label }}</div>
          </div>`
        ),
        row: (
          `<div class='row'>
            <div class='col-sm-2'>{{ flattenedComponents.target.getView(row.target) }}</div>
            <div class='col-sm-2'>{{ flattenedComponents.description.getView(row.description) }}</div>
            {% if (!instance.options.readOnly) { %}
              <div class='col-sm-2'>
                <div class='btn-group pull-right'>
                  <button class='btn btn-primary btn-sm editRow'>Edit</button>
                  <button class='btn btn-danger btn-sm removeRow'>Delete</button>
                </div>
              </div>
            {% } %}
          </div>`
        ),
      },
      components: [
        {
          label: 'Target',
          tableView: true,
          type: 'select',
          input: true,
          key: 'target',
          template: '<span>{{ item.label }}</span>',
          data: {
            values: components,
          },
        },
        {
          label: 'Description',
          tableView: true,
          type: 'textfield',
          input: true,
          key: 'description',
          conditional: {
            json: {
              '!!': {
                var: 'row.target',
              },
            },
          },
        },
        {
          label: 'Logic',
          tableView: true,
          type: 'tree',
          input: true,
          key: 'logic',
          conditional: {
            json: {
              '!!': {
                var: 'row.target',
              },
            },
          },
          components: [
            {
              label: 'Operator',
              tableView: true,
              type: 'select',
              input: true,
              key: 'operator',
              dataType: 'string',
              data: {
                values: Object.entries(Utils.ValidationService.operatorsLimits).map((item) => (
                  {
                    label: _.startCase(item[0]),
                    value: item[0],
                  }
                )),
              },
            },
            {
              label: 'Description',
              tableView: true,
              type: 'textfield',
              input: true,
              key: 'description',
              conditional: {
                json: {
                  '!': {
                    in: [
                      {
                        var: 'row.operator',
                      },
                      [
                        'formComponentValue',
                        'nestedComponentValue',
                        'parentComponentValue',
                        'value',
                        'index',
                        'text',
                        'number',
                        'true',
                        'false',
                        'null',
                        'errorMessage',
                        'getFormPath',
                        'getNestedPath',
                        'pattern',
                      ],
                    ],
                  },
                },
              },
            },
            {
              label: 'Form Component',
              tableView: true,
              type: 'select',
              input: true,
              key: 'formComponent',
              conditional: {
                json: {
                  in: [
                    {
                      var: 'row.operator',
                    },
                    [
                      'formComponentValue',
                      'getFormPath',
                    ],
                  ],
                },
              },
              data: {
                values: components,
              },
            },
            {
              label: 'Nested Component',
              tableView: true,
              type: 'select',
              input: true,
              key: 'nestedComponent',
              conditional: {
                json: {
                  in: [
                    {
                      var: 'row.operator',
                    },
                    [
                      'nestedComponentValue',
                      'getNestedPath',
                    ],
                  ],
                },
              },
              dataSrc: 'custom',
              data: {
                custom({
                  instance,
                }) {
                  return arrayComponentsMap[instance.parent.data.target] || [];
                },
              },
            },
            {
              label: 'Parent Component',
              tableView: true,
              type: 'select',
              input: true,
              key: 'parentComponent',
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'parentComponentValue',
                  ],
                },
              },
              dataSrc: 'custom',
              data: {
                custom({
                  _: lodash,
                  instance,
                  row,
                }) {
                  const parent = lodash.times(row.level).reduce(
                    (result) => parentComponentsMap[result] || '',
                    parentComponentsMap[instance.parent.data.target] || '',
                  );

                  return arrayComponentsMap[parent] || [];
                },
              },
            },
            {
              label: 'Text',
              tableView: true,
              type: 'textfield',
              input: true,
              key: 'text',
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'text',
                  ],
                },
              },
            },
            {
              label: 'Number',
              tableView: true,
              type: 'number',
              input: true,
              key: 'number',
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'number',
                  ],
                },
              },
            },
            {
              label: 'Error Message',
              tableView: true,
              type: 'textfield',
              input: true,
              key: 'errorMessage',
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'errorMessage',
                  ],
                },
              },
            },
            {
              label: 'Component',
              tableView: true,
              type: 'select',
              input: true,
              key: 'errorComponents',
              multiple: true,
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'errorMessage',
                  ],
                },
              },
              data: {
                values: components,
              },
            },
            {
              label: 'Warning',
              tableView: true,
              type: 'checkbox',
              input: true,
              key: 'warning',
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'errorMessage',
                  ],
                },
              },
            },
            {
              label: 'Disabled',
              tableView: true,
              type: 'checkbox',
              input: true,
              key: 'disabled',
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'assert',
                  ],
                },
              },
            },
            {
              label: 'Level',
              tableView: true,
              type: 'number',
              input: true,
              key: 'level',
              defaultValue: 0,
              conditional: {
                json: {
                  in: [
                    {
                      var: 'row.operator',
                    },
                    [
                      'index',
                      'parentComponentValue',
                    ],
                  ],
                },
              },
            },
            {
              label: 'Pattern',
              tableView: true,
              type: 'select',
              input: true,
              key: 'pattern',
              dataSrc: 'values',
              data: {
                values: Utils.ValidationService.Patterns,
              },
              conditional: {
                json: {
                  '===': [
                    {
                      var: 'row.operator',
                    },
                    'pattern',
                  ],
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

const setRoot = ({ root }) => {
  root.eachChild(node => Utils.ValidationService.applyLimits(Utils.ValidationService.getNodeLimits(node), node));
};

const addChild = ({ parent, component }) => {
  Utils.ValidationService.addChildren(parent, Utils.ValidationService.getNodeLimits(parent).step);
  component.redraw();
};

const cancelNode = ({ node, component }) => {
  if (node.isRoot) {
    component.removeRoot();
  } else {
    const { parent } = node;
    const limits = Utils.ValidationService.getNodeLimits(parent);
    Utils.ValidationService.getSiblings(node, limits.step).forEach(subling => subling.cancel());
    Utils.ValidationService.applyLimits(limits, parent);
    component.redraw();
  }
};

const removeNode = ({ node, component }) => {
  if (node.isRoot) {
    component.removeRoot();
  } else {
    const { parent } = node;
    const limits = Utils.ValidationService.getNodeLimits(parent);
    Utils.ValidationService.getSiblings(node, limits.step).forEach(subling => subling.remove());
    Utils.ValidationService.applyLimits(limits, parent);
    component.updateTree();
  }
};

const revertNode = ({ node, component }) => {
  node.revert();
  Utils.ValidationService.applyLimits(Utils.ValidationService.getNodeLimits(node), node);
  component.updateTree();
};

const saveNode = ({ node, component }) => {
  node.save();
  Utils.ValidationService.applyLimits(Utils.ValidationService.getNodeLimits(node), node);
  component.updateTree();
};

export default class Validation extends React.Component<ValidationProps, ValidationState> {
  constructor(props){
    super(props)
    this.state = { form: this.props.form, 
                   submission: createValidationSubmission(this.props.form, this.props.submission),
                   searchText: '',
                   statusFilter: 'all',
                   viewMode: 'all',
                   sampleData: '{\n  "data": {}\n}',
                   testResults: undefined,
                   testError: undefined,
                };
  }

  getComponents = () => [
    {
      label: "Form",
      value: "form",
      component: this.state.form
    }
  ].concat(
    _.map(Utils.flattenComponents(this.state.form.components || [], true), (component, path) => ({
      label: `${component.label || component.key} (${path})`,
      value: path,
      component
    }))
  );

  getFilteredSummaries = (summaries: ValidationSummary[]) => {
    const searchText = this.state.searchText.trim().toLowerCase();

    return summaries.filter(summary => {
      const matchesSearch = !searchText || summary.searchableText.includes(searchText);
      const matchesStatus =
        this.state.statusFilter === 'all' ||
        (this.state.statusFilter === 'error' && summary.type === 'error') ||
        (this.state.statusFilter === 'warning' && summary.type === 'warning') ||
        (this.state.statusFilter === 'disabled' && summary.disabled) ||
        (this.state.statusFilter === 'issues' && summary.issues.length > 0);
      const matchesView = this.state.viewMode !== 'pending' || !!summary.changeStatus;

      return matchesSearch && matchesStatus && matchesView;
    });
  };

  runValidationTest = () => {
    try {
      const parsed = JSON.parse(this.state.sampleData || '{}');
      const data = parsed && parsed.data ? parsed.data : parsed;
      const validation = Utils.ValidationService.translateValidationToJsonLogic(getValidationList(this.state.submission));
      const testResults = Utils.ValidationService.validate(validation, data) || [];

      this.setState({
        testResults: Array.isArray(testResults) ? testResults : [testResults],
        testError: undefined,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      this.setState({
        testResults: undefined,
        testError: message ? message : 'Sample data is not valid JSON.',
      });
    }
  };

  renderSummaryWorkspace = (summaries: ValidationSummary[]) => {
    const filteredSummaries = this.getFilteredSummaries(summaries);
    const issueCount = summaries.filter(summary => summary.issues.length > 0).length;
    const disabledCount = summaries.filter(summary => summary.disabled).length;
    const warningCount = summaries.filter(summary => summary.type === 'warning').length;
    const errorCount = summaries.filter(summary => summary.type === 'error').length;
    const pendingCount = summaries.filter(summary => !!summary.changeStatus).length;

    return (
      <div className='validation-workspace'>
        <div className='validation-toolbar'>
          <div>
            <h3>{this.state.form.title} Validations</h3>
            <div className='validation-counts'>
              <Badge variant='secondary'>{summaries.length} total</Badge>
              <Badge variant='danger'>{errorCount} errors</Badge>
              <Badge variant='warning'>{warningCount} warnings</Badge>
              <Badge variant='dark'>{disabledCount} disabled</Badge>
              <Badge variant={issueCount > 0 ? 'danger' : 'success'}>{issueCount} need review</Badge>
            </div>
            <Nav variant='tabs' className='validation-tabs' activeKey={this.state.viewMode}
              onSelect={(key: any) => this.setState({ viewMode: key === 'pending' ? 'pending' : 'all' })}>
              <Nav.Item>
                <Nav.Link eventKey='all'>All Validations</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey='pending'>
                  Pending Changes{' '}
                  <Badge variant={pendingCount > 0 ? 'primary' : 'light'}>{pendingCount}</Badge>
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
          <div className='validation-filters'>
            <BootstrapForm.Control
              aria-label='Search validations'
              placeholder='Search target, message, or logic'
              value={this.state.searchText}
              onChange={(event: any) => this.setState({ searchText: event.target.value })}
            />
            <BootstrapForm.Control
              as='select'
              aria-label='Filter validations'
              value={this.state.statusFilter}
              onChange={(event: any) => this.setState({ statusFilter: event.target.value })}
            >
              <option value='all'>All validations</option>
              <option value='error'>Errors</option>
              <option value='warning'>Warnings</option>
              <option value='disabled'>Disabled</option>
              <option value='issues'>Needs review</option>
            </BootstrapForm.Control>
          </div>
        </div>

        <Row>
          <Col lg={8}>
            {this.state.viewMode === 'pending' && pendingCount === 0 && (
              <div className='validation-empty'>
                No pending changes. New and edited rules appear here until the form is saved.
              </div>
            )}
            <ValidationSummaryList
              summaries={filteredSummaries}
              onView={this.state.viewMode === 'pending' ? (summary => this.setState({ jsonPreviewSummary: summary })) : undefined}
            />
          </Col>
          <Col lg={4}>
            <div className='validation-test-panel'>
              <h4>Test Current Rules</h4>
              <BootstrapForm.Control
                as='textarea'
                rows={10}
                value={this.state.sampleData}
                onChange={(event: any) => this.setState({ sampleData: event.target.value })}
              />
              <Button variant='secondary' size='sm' className='validation-test-button' onClick={this.runValidationTest}>
                Run test
              </Button>
              {this.state.testError && <div className='validation-test-error'>{this.state.testError}</div>}
              {this.state.testResults && (
                <div className='validation-test-results'>
                  <strong>{this.state.testResults.length} result{this.state.testResults.length === 1 ? '' : 's'}</strong>
                  {this.state.testResults.map((result, index) => (
                    <pre key={index}>{JSON.stringify(result, null, 2)}</pre>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>

        <Modal show={!!this.state.jsonPreviewSummary} onHide={() => this.setState({ jsonPreviewSummary: undefined })} size='lg'>
          <Modal.Header closeButton>
            <Modal.Title>
              Rule JSON{this.state.jsonPreviewSummary ? ` — #${this.state.jsonPreviewSummary.displayNumber}` : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <pre className='validation-json-preview'>
              {this.state.jsonPreviewSummary
                ? JSON.stringify(this.state.jsonPreviewSummary.rawRule, null, 2)
                : ''}
            </pre>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => this.setState({ jsonPreviewSummary: undefined })}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };
  
  render() {
    if (!this.state.form || !this.state.submission) {
      return <div />;
    }

    const components = this.getComponents();

    const arrayComponentsMap = {};
    const parentComponentsMap = {};
    components
      .filter(({ component }) => ["form", "datagrid", "editgrid"].includes(component.type))
      .forEach(({ component: parentComponent, value: parentPath }) => {
        arrayComponentsMap[parentPath] = _.map(
          Utils.flattenComponents(parentComponent.components, true),
          (component, path) => {
            const fullPath = `${parentPath}.${path}`;
            const parent = parentComponentsMap[fullPath];

            if (!parent || parent.length < parentPath.length) {
              parentComponentsMap[fullPath] = parentPath;
            }

            return {
              label: `${component.label || component.key} (${path})`,
              value: path
            };
          }
        );
      });
    const validationForm = validationFormFactory(components, arrayComponentsMap, parentComponentsMap);
    const rawSummaries = buildValidationSummaries(getValidationList(this.state.submission), components);
    const baselineSummaries = Array.isArray(this.props.previousValidation)
      ? buildValidationSummaries(this.props.previousValidation, components)
      : undefined;
    const validationSummaries = annotateChangeStatus(rawSummaries, baselineSummaries);

    return (
      <>
        {this.renderSummaryWorkspace(validationSummaries)}
        <div className='validation-editor-heading'>
          <h4>Advanced Rule Editor</h4>
          <span>Edit the selected validation rules below. Changes update the summary above immediately.</span>
        </div>
        <div className='validation-advanced-editor'>
          <Form
            form={validationForm}
            submission={this.state.submission}
            options={{
              templates: {
                treeView: {
                  form: `<div class="validation-tree-node validation-tree-node-{{ nodeData.operator || 'empty' }} {{ nodeData.disabled ? 'validation-tree-node-disabled' : '' }}">
                      <div class="validation-tree-main">
                        <div class="validation-tree-title">
                          <span class="validation-tree-operator">{{ values[0] || 'Incomplete' }}</span>
                          {% if (node.limits.max !== 0 && node.children.length > 0) { %}
                            <span class="validation-tree-child-count">{{ node.children.length }} child{{ node.children.length === 1 ? '' : 'ren' }}</span>
                          {% } %}
                          {% if (nodeData.operator === 'errorMessage') { %}
                            <span class="validation-tree-kind validation-tree-kind-{{ nodeData.warning ? 'warning' : 'error' }}">{{ nodeData.warning ? 'Warning' : 'Error' }}</span>
                          {% } %}
                          {% if (nodeData.disabled) { %}
                            <span class="validation-tree-kind validation-tree-kind-disabled">Disabled</span>
                          {% } %}
                        </div>
                        <div class="validation-tree-detail">
                          {% if (node.limits.max === 0) { %}
                            {% if (nodeData.operator === 'formComponentValue' || nodeData.operator === 'getFormPath') { %}
                              <span class="validation-tree-field">{{ values[2] }}</span>
                            {% } else if (nodeData.operator === 'nestedComponentValue' || nodeData.operator === 'getNestedPath') { %}
                              <span class="validation-tree-field">{{ values[3] }}</span>
                            {% } else if (nodeData.operator === 'parentComponentValue') { %}
                              <span class="validation-tree-field">{{ values[4] }}</span>
                            {% } else if (nodeData.operator === 'text') { %}
                              <span class="validation-tree-literal">{{ values[5] }}</span>
                            {% } else if (nodeData.operator === 'number') { %}
                              <span class="validation-tree-literal">{{ values[6] }}</span>
                            {% } else if (nodeData.operator === 'errorMessage') { %}
                              <span class="validation-tree-message">{{ values[7] || 'Missing message' }}</span>
                              {% if (nodeData.errorComponents && nodeData.errorComponents.length > 0) { %}
                                <span class="validation-tree-components">{{ values[8] }}</span>
                              {% } %}
                            {% } else if (nodeData.operator === 'index') { %}
                              <span class="validation-tree-literal">{{ values[11] }}</span>
                            {% } else if (nodeData.operator === 'pattern') { %}
                              <span class="validation-tree-literal">{{ values[12] }}</span>
                            {% } else { %}
                              <span class="validation-tree-muted">{{ values[1] }}</span>
                            {% } %}
                          {% } else if (values[1]) { %}
                            <span class="validation-tree-description">{{ values[1] }}</span>
                          {% } else { %}
                            <span class="validation-tree-muted">Operator node</span>
                          {% } %}
                        </div>
                      </div>
                      {% if (!instance.options.readOnly) { %}
                        <div class='btn-group validation-group'>
                          {% if (!node.hasChangingChildren && node.limits.max !== 0 && node.children.length > 0) { %}
                            <button ref="toggleNode" class="btn btn-primary btn-sm toggleNode" title="{{ node.collapsed ? 'Expand' : 'Collapse' }}">
                              <i class="fa fa-caret-{{ node.collapsed ? 'down' : 'up' }}"></i>
                            </button>
                          {% } %}
                          {% if (node.limits.max < 0 || node.children.length < node.limits.max) { %}
                            <button ref="addChild" class="btn btn-primary btn-sm addChild" title="Add">
                              <i class="fa fa-plus"></i>
                            </button>
                          {% } %}
                          {% if (!node.hasChangingChildren) { %}
                            <button ref="editNode" class="btn btn-primary btn-sm editNode" title="Edit">
                              <i class="fa fa-edit"></i>
                            </button>
                          {% } %}
                          <button ref="removeNode" class="btn btn-danger btn-sm removeNode" title="Delete">
                            <i class="fa fa-times"></i>
                          </button>
                          {% if (!node.hasChangingChildren && node.revertAvailable) { %}
                            <button ref="revertNode" class="btn btn-danger btn-sm revertNode" title="Revert">
                              <i class="fa fa-undo"></i>
                            </button>
                          {% } %}
                        </div>
                      {% } %}
                    </div>`
                }
              },
              hooks: {
                "tree.setRoot": setRoot,
                "tree.addChild": addChild,
                "tree.saveNode": saveNode,
                "tree.revertNode": revertNode,
                "tree.removeNode": removeNode,
                "tree.cancelNode": cancelNode
              }
            }}
            onChange={(data, metadata, changed, isValid) => {
              if (!changed) {
                return;
              }

              const nextValidation = normalizeValidationValue(_.get(data, 'data.validation'));
              const currentValidation = getValidationList(this.state.submission);

              if (_.isEqual(nextValidation, currentValidation)) {
                return;
              }

              this.setState({
                form: { ...this.state.form, validation: nextValidation},
                submission: {
                  ...data,
                  data: {
                    ..._.get(data, 'data', {}),
                    validation: nextValidation,
                  },
                },
                testResults: undefined,
                testError: undefined,
              });
              this.props.setValidation(nextValidation);
              /*
              this.props.setEditFormConfig({
                ...this.props.editFormConfig,
                formModel: {
                  ...this.props.editFormConfig.formModel,
                  properties: { ...this.props.editFormConfig.formModel.properties, validation: data.data.validation }
                }
              });
              */
            }}
          />
        </div>
      </>
    );
  }

  componentDidMount() {
    this.setState({
      form: this.props.form,
      submission: createValidationSubmission(this.props.form, this.props.submission),
    });
  }

  componentDidUpdate(prevProps: ValidationProps) {
    if (prevProps.form !== this.props.form || prevProps.submission !== this.props.submission) {
      this.setState({
        form: this.props.form,
        submission: createValidationSubmission(this.props.form, this.props.submission),
      });
    }
  }

  shouldComponentUpdate(nextProps: ValidationProps, nextState: ValidationState) {
    return true;
  }
}

/*
const mapStateToProps = (state: StateTree): ValidationReduxProps => ({
  form: state.FormConfiguration.editFormConfig && state.FormConfiguration.editFormConfig.formModel,
  editFormConfig: state.FormConfiguration.editFormConfig,
  submission: {
    data: {
      validation:
        (state.FormConfiguration.editFormConfig &&
          state.FormConfiguration.editFormConfig.formModel &&
          state.FormConfiguration.editFormConfig.formModel.properties &&
          state.FormConfiguration.editFormConfig.formModel.properties.validation) ||
        []
    }
  }
});

const mapDispatchToProps: ValidationDispatchProps = {
  setEditFormConfig
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Validation);
*/
